import { getDaysInMonth } from 'date-fns'
import { useEffect, useState, useCallback } from 'react'

type DateSelection = {
  day: number
  month: number
  year: number
}

const generateYears = (minYear?: number, maxYear?: number) => {
  const year = new Date().getFullYear()
  const years: number[] = []

  for (var i = 10; i > -10; i--) {
    const y = year - i
    if ((!minYear || (minYear && y >= minYear)) && (!maxYear || y <= maxYear)) {
      years.push(year - i)
    }
  }

  return years
}

export const parseDateString = (date: string) => {
  const matches = date ? date.match(/(\d{4})-(\d\d)-(\d\d)/) : null
  if (!matches) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_match, year, month, day] = matches

  return {
    day: parseInt(day, 10),
    month: parseInt(month, 10),
    year: parseInt(year, 10),
  }
}

export const serializeDateString = ({ day, month, year }: DateSelection) =>
  `${year}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`

export const useDateSelection = (
  initialValue: DateSelection,
  min?: string,
  max?: string
) => {
  const [touched, setTouched] = useState<boolean>(false)
  const touch = useCallback(() => setTouched(true), [setTouched])

  const [dates, setDate] = useState(initialValue)
  const { day, month, year } = dates

  const minYear = min ? new Date(min).getFullYear() : undefined
  const maxYear = max ? new Date(max).getFullYear() : undefined

  const dayCount = getDaysInMonth(new Date(year, month - 1, 1))
  const days = Array.from(Array(dayCount), (_, i) => i + 1)
  const months = Array.from(Array(12), (_, i) => i + 1)
  const years = generateYears(minYear, maxYear)

  useEffect(() => {
    if (day > dayCount) {
      setDate((d) => ({ ...d, day: dayCount }))
    }
  }, [day, dayCount])

  const setYear = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target
      setDate((d) => ({ ...d, year: parseInt(value) }))
      touch()
    },
    [setDate, touch]
  )
  const setMonth = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target
      setDate((d) => ({ ...d, month: parseInt(value) }))
      touch()
    },
    [setDate, touch]
  )
  const setDay = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target
      setDate((d) => ({ ...d, day: parseInt(value) }))
      touch()
    },
    [setDate, touch]
  )

  return {
    dates,
    setYear,
    setMonth,
    setDay,
    days,
    months,
    years,
    touched,
  }
}
